/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useEffect } from 'react';
import type { Logger } from '@feature-hub/logger';
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  createHttpLink,
} from '@apollo/client';
import { concat } from '@apollo/client/link/core';
import { v4 as uuidv4 } from 'uuid';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import type { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import type { EnvConfigServiceV1 } from '@volkswagen-onehub/audi-env-config-service';
import type { TrackingServiceV2 } from '@volkswagen-onehub/audi-tracking-service';
import { getGraphqlGatewayUrl, getOneGraphGatewayUrl } from './hooks/getGraphqlGatewayUrl';
import VehicleProvider from './components/VehicleProvider';
import i18n from '../i18n';
import ContentWrapper from './components/ContentWrapper';
import ErrorBoundary from './components/ErrorBoundary';
import { IS_TIER3_PAGE } from './constants';
import { IS_MOCK } from './constants/environment';

interface FeatureAppProps {
  configProvider?: GfaServiceConfigProviderV1;
  trackingService?: TrackingServiceV2;
  loggerServices?: Logger;
  envConfigService: EnvConfigServiceV1;
}

const correlationIdLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'x-correlation-id': uuidv4(),
    },
  }));

  return forward(operation);
});

const httpLink = createHttpLink({
  uri: getGraphqlGatewayUrl(),
});

const httpOneGraphLink = createHttpLink({
  uri: getOneGraphGatewayUrl(),
});

const apolloClient = new ApolloClient({
  link: concat(correlationIdLink, httpLink),
  cache: new InMemoryCache(),
  name: __FEATURE_APP_NAME__,
  version: __FEATURE_APP_VERSION__,
});

const oneGraphClient = new ApolloClient({
  link: concat(correlationIdLink, httpOneGraphLink),
  cache: new InMemoryCache(),
  name: __FEATURE_APP_NAME__,
  version: __FEATURE_APP_VERSION__,
});

const FeatureApp: React.FC<FeatureAppProps> = ({ trackingService }: FeatureAppProps) => {
  useEffect(() => {
    if (!trackingService) {
      return;
    }
    const readyEvent = {
      event: {
        attributes: {
          componentName: __FEATURE_APP_NAME__,
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: '',
        },
        eventInfo: {
          eventAction: 'feature_app_ready',
          eventName: `${__FEATURE_APP_NAME__} - Feature App ready`,
        },
      },
    };
    trackingService.track(readyEvent);
  }, [trackingService]);

  const locationSearch = typeof window !== 'undefined' ? window.location.search : '';
  const vehicleId = new URLSearchParams(locationSearch).get('vehicleId') ?? undefined;
  const market = new URLSearchParams(locationSearch).get('market') ?? 'us';

  return (
    <I18nextProvider i18n={i18n}>
      <ErrorBoundary>
        <ThemeProvider>
          <ApolloProvider client={apolloClient}>
            <VehicleProvider isTier3={IS_TIER3_PAGE}>
              <ContentWrapper
                vehicleId={vehicleId}
                market={market}
                isTier3={IS_TIER3_PAGE}
                isMock={IS_MOCK}
                oneGraphClient={oneGraphClient}
              />
            </VehicleProvider>
          </ApolloProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </I18nextProvider>
  );
};

export default FeatureApp;
